import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  fetchDataThunk,
  fetchRealTimeLineActivityThunk,
  fetchRealTimeLineActivityNonSeed,
  fetchOrganizationThunk,
} from "../../store/reports/thunk";
import { setDate } from "../../store/reports/actions";
import "../../styles/global.css";
import noDataImage from "../../assets/no_data.png";
import AsyncStorage from "@react-native-async-storage/async-storage";
import {
  Home,
  PauseCircle,
  PlayCircle,
  ChevronLeftCircle,
  ChevronRightCircle,
} from "lucide-react";
import { APP_CONSTANTS } from "../../utilities/app_constants";

const RealTimeLineActivity = ({
  reportData,
  onNext,
  onPrevious,
  onPause,
  isPaused,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [params, setParams] = useState({});
  const [organizationFetched, setOrganizationFetched] = useState(false);
  const [orgLoading, setOrgLoading] = useState(false);

  const {
    data,
    loading,
    error,
    organizationData,
    currentDate,
    realTimeLineActivityData,
    realTimeLineActivityNonSeed,
  } = useSelector((state) => state.report);

  useEffect(() => {
    if (reportData?.url) {
      const urlParams = new URLSearchParams(reportData.url.split("?")[1]);
      const extractedParams = {
        orgId: urlParams.get("AD_Org_ID"),
        lineMasterId: urlParams.get("DGM_Newlinemaster_ID"),
        note: urlParams.get("Note"),
      };
      setParams(extractedParams);
    }
  }, [reportData]);

  const getCurrentDate = () => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  const ProcesstypeId = data?.[0]?.ProcesstypeId || null;

  useEffect(() => {
    if (params.orgId && params.lineMasterId) {
      const date = getCurrentDate();
      dispatch(setDate(date));
      dispatch(fetchDataThunk(params.orgId, params.lineMasterId));
      dispatch(
        fetchRealTimeLineActivityThunk(
          params.orgId,
          params.lineMasterId,
          ProcesstypeId
        )
      );
      dispatch(
        fetchRealTimeLineActivityNonSeed(
          params.orgId,
          params.lineMasterId,
          ProcesstypeId
        )
      );
    }
  }, [dispatch, ProcesstypeId, params]);

  useEffect(() => {
    const warehouse = data?.[0]?.Warehouse;
    const lineNo = data?.[0]?.LineNo;
    
    if ((!warehouse || warehouse === null || warehouse === "") && 
        (!lineNo || lineNo === null || lineNo === "") && 
        params.orgId &&
        !organizationFetched &&
        !orgLoading) {
      setOrgLoading(true);
      dispatch(fetchOrganizationThunk(params.orgId));
      setOrganizationFetched(true);
    }
  }, [data, params.orgId, dispatch, organizationFetched, orgLoading]);

  const getOrganizationName = () => {
    if (!organizationData) return null;
    
    if (organizationData.response?.data?.[0]?.name) {
      return organizationData.response.data[0].name;
    }
    if (organizationData.data?.[0]?.name) {
      return organizationData.data[0].name;
    }
    if (organizationData.name) {
      return organizationData.name;
    }
    if (organizationData.response?.name) {
      return organizationData.response.name;
    }
    
    return null;
  };

  const warehouse = data?.[0]?.Warehouse;
  const lineNo = data?.[0]?.LineNo;
  
  const organizationName = getOrganizationName();
  const isWarehouseNull = (!warehouse || warehouse === null || warehouse === "");
  const isLineNoNull = (!lineNo || lineNo === null || lineNo === "");
  const shouldShowOrganization = isWarehouseNull && isLineNoNull;
  
  let displayWarehouse;
  let warehouseLabel = "Warehouse:";
  let showLineNo = true;
  
  if (shouldShowOrganization && organizationName) {
    displayWarehouse = organizationName;
    warehouseLabel = "Organization:";
    showLineNo = false;
  } else if (shouldShowOrganization && orgLoading) {
    displayWarehouse = "Loading...";
    warehouseLabel = "Organization:";
    showLineNo = false;
  } else if (!isWarehouseNull) {
    displayWarehouse = warehouse;
    warehouseLabel = "Warehouse:";
    showLineNo = true;
  } else {
    displayWarehouse = "-";
    warehouseLabel = "Warehouse:";
    showLineNo = true;
  }
  
  const displayLineNo = (showLineNo && !isLineNoNull && lineNo) ? lineNo : "";

  useEffect(() => {
    if (!isPaused) {
      const intervalInSeconds = parseInt(reportData?.interval, 10);
      const displayTime =
        !isNaN(intervalInSeconds) && intervalInSeconds > 0
          ? intervalInSeconds * 1000
          : APP_CONSTANTS.REPORT_DISPLAY_TIME;

      const timeout = setTimeout(() => {
        onNext();
      }, displayTime);

      return () => clearTimeout(timeout);
    }
  }, [onNext, isPaused, reportData?.interval]);

  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.textContent = `
      .container {
        max-width: 100%;
        overflow-x: hidden;
      }
      
      @media (max-width: 1024px) {
        .header {
          flex-wrap: wrap !important;
          justify-content: center !important;
        }
        
        .header .home-button {
          order: 1;
        }
        
        .header .controls {
          order: 2;
        }
        
        .header h1 {
          order: 3;
          width: 100%;
          text-align: center;
          margin: 10px 0 !important;
        }
        
        .header .header-info {
          order: 4;
          width: 100%;
          justify-content: center !important;
          margin: 8px 0 !important;
        }
        
        .header .date-display {
          order: 5;
          margin: 8px auto !important;
        }
        
        .header .logo-container {
          order: 6;
          margin: 5px 0 !important;
        }
      }
      
      @media (max-width: 768px) {
        .header .controls svg {
          width: 32px !important;
          height: 32px !important;
        }
        
        .header h1 {
          font-size: 18px !important;
        }
        
        .header .header-info span {
          font-size: 12px !important;
        }
        
        .current-date {
          width: 70px !important;
          font-size: 11px !important;
        }
      }
      
      @media (max-width: 480px) {
        .header .controls {
          gap: 5px !important;
        }
        
        .header .controls svg {
          width: 28px !important;
          height: 28px !important;
        }
        
        .header h1 {
          font-size: 16px !important;
        }
        
        .current-date {
          width: 60px !important;
          font-size: 10px !important;
        }
      }
      
      .rtl-main-table-container {
        overflow-x: auto;
        width: 100%;
      }
      
      .rtl-main-table {
        min-width: 800px;
      }
      
      .rtl-secondary-tables {
        display: flex;
        flex-direction: column;
        gap: 20px;
        margin-Left:10px;
      }
      
      .rtl-secondary-tables > div {
        width: 100% !important;
        overflow-x: auto !important;
      }
      
      @media (max-width: 768px) {
        .rtl-data-table {
          font-size: 12px;
        }
        
        .rtl-data-table th,
        .rtl-data-table td {
          padding: 6px !important;
        }
      }
      
      @media (max-width: 480px) {
        .rtl-data-table th,
        .rtl-data-table td {
          padding: 4px !important;
          font-size: 10px !important;
        }
        
        .rtl-header-column {
          font-size: 10px !important;
          min-width: 100px !important;
        }
      }
      
      @media (max-width: 768px) {
        .marquee-container {
          font-size: 14px !important;
          bottom: 20px !important;
        }
        
        .marquee-container div span {
          padding-right: 60px !important;
        }
      }
      
      @media (max-width: 480px) {
        .marquee-container {
          font-size: 11px !important;
          bottom: 10px !important;
          padding: 8px 0 !important;
        }
        
        .marquee-container div span {
          padding-right: 40px !important;
        }
      }
      
      .rtl-table-layout {
        width: 100%;
      }
      
      .rtl-data-table {
        border-collapse: collapse;
        width: 100%;
      }
      
      .rtl-data-table th,
      .rtl-data-table td {
        border: 1px solid #ddd;
        padding: 8px;
        text-align: left;
      }
      
      .rtl-data-table th {
        background-color: linear-gradient(135deg, #0A2A88 0%, #1565C0 100%);
        font-weight: bold;
        color:#fff;
      }
      
      .rtl-header-column {
        background-color: #e8e8e8;
        font-weight: bold;
      }
      
      .rtl-table-header {
        background-color: #0070C0;
        color: white;
        text-align: center;
        font-weight: bold;
      }
      
      .rtl-table-container {
        margin-bottom: 20px;
      }
    `;
    document.head.appendChild(styleSheet);
    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  const columnMappings = {
    StartTime: "Start Date & Time",
    Endtime: "Stop Date & Time",
    LineSupervisor: "Line Supervisor",
    qrono: "QRO No.",
    DocumentNo: "Document No.",
    IsueeType: "Issue Type",
    OutputVariety: "Output Variety",
    outputbatchNo: "Output Batch No.",
    WorkOrder: "Work Order No.",
    PackSize: "Packing Size",
    ScreenSize: "Screen Size",
    MRP: "MRP",
    InputeQty: "Input Qty",
    outputQty: "Output Qty",
    RecoveryQty: "Recovery %",
    "RemnantQty ": "Remnant Qty %",
    LossQty: "Loss Qty %",
    ApprovalStatus: "Approval Status & Next",
    TodayOutputQty: "Today's Output Qty",
    Remark: "Remark",
  };

  const scrollingText = `${params?.note || ""}`;
  
  return (
    <div className="container" style={{ position: "relative", minHeight: "100vh" }}>
      <header className="header" style={{ flexWrap: "wrap", gap: "1px", display: "flex", alignItems: "center" }}>
        <button
          className="home-button"
          onClick={() =>
            navigate("/menu", {
              state: { hideModal: true },
            })
          }
        >
          <Home
            className="home-icon"
            size={120}
            style={{
              width: "35px",
              height: "40px",
            }}
          />
        </button>
        
        <div className="controls" style={{ display: "flex", gap: "1px", alignItems: "center" }}>
          <ChevronLeftCircle
            size={40}
            onClick={onPrevious}
            style={{ cursor: "pointer" }}
          />
          {isPaused ? (
            <PlayCircle
              size={40}
              onClick={() => onPause(false)}
              style={{ cursor: "pointer" }}
            />
          ) : (
            <PauseCircle
              size={40}
              onClick={() => onPause(true)}
              style={{ cursor: "pointer" }}
            />
          )}
          <ChevronRightCircle
            size={40}
            onClick={onNext}
            style={{ cursor: "pointer" }}
          />
        </div>
        
        <h1 style={{ fontSize: "clamp(16px, 4vw, 28px)", margin: "5px 0" }}>
          Real Time Line Activity
        </h1>
        
        <div
          className="header-info"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexWrap: "wrap",
            gap: "9px",
            marginTop: "1px",
          }}
        >
          <span
            style={{
              background: "linear-gradient(90deg, #0A2A88, #1565C0)",
              color: "#FFFFFF",
              padding: "6px 14px",
              borderRadius: "30px",
              fontSize: "clamp(14px, 2vw, 28px)",
              fontWeight: "800",
              display: "inline-flex",
              alignItems: "center",
              gap: "10px",
              border: "2px solid rgba(255,255,255,0.7)",
              letterSpacing: "0.5px",
              boxShadow:
                "0 0 10px rgba(255,255,255,0.2), 0 4px 10px rgba(0,0,0,0.4)",
            }}
          >
            <span
              style={{
                color: "#FFFFFF",
                fontWeight: "700",
                textTransform: "uppercase",
                opacity: 0.9,
                textShadow: "0 0 5px black",
              }}
            >
              {warehouseLabel}
            </span>
            <span
              style={{
                width: "2px",
                height: "22px",
                background: "rgba(255,255,255,0.5)",
              }}
            />
            <span
              style={{
                color: "#FFFFFF",
                fontSize: "clamp(12px, 3vw, 28px)",
                fontWeight: "bold",
                textShadow: "0 0 8px black, 0 0 12px black",
                wordBreak: "break-word",
                maxWidth: "400px",
              }}
            >
              {displayWarehouse}
            </span>
          </span>

          {showLineNo && displayLineNo && (
            <span
              style={{
                background: "linear-gradient(90deg, #0A2A88, #1565C0)",
                color: "#FFFFFF",
                padding: "6px 14px",
                borderRadius: "30px",
                fontSize: "clamp(14px, 2vw, 28px)",
                fontWeight: "800",
                display: "inline-flex",
                alignItems: "center",
                gap: "10px",
                border: "2px solid rgba(255,255,255,0.7)",
                letterSpacing: "0.5px",
                boxShadow:
                  "0 0 10px rgba(255,255,255,0.2), 0 4px 10px rgba(0,0,0,0.4)",
              }}
            >
              <span
                style={{
                  color: "#BBDEFB",
                  fontWeight: "700",
                  textTransform: "uppercase",
                  opacity: 0.9,
                  textShadow: "0 0 5px black",
                }}
              >
                Line No
              </span>
              <span
                style={{
                  width: "2px",
                  height: "22px",
                  background: "rgba(255,255,255,0.5)",
                }}
              />
              <span
                style={{
                  color: "#FFFFFF",
                  fontSize: "clamp(12px, 3vw, 28px)",
                  fontWeight: "bold",
                  textShadow: "0 0 8px black, 0 0 12px black",
                }}
              >
                {displayLineNo}
              </span>
            </span>
          )}
        </div>
        
        <div className="date-display" style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span>Current Date:</span>
          <input
            type="text"
            value={currentDate}
            readOnly
            className="current-date"
            style={{
              width: "80px",
              minWidth: "80px",
              padding: "4px 8px",
              fontSize: "14px",
            }}
          />
        </div>
        
        <div className="logo-container">
          <a
            href="https://www.digiopenservices.com/"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              src="/Logo.png"
              alt="Logo"
              className="header-logo"
              style={{
                maxwidth: "110px",
                height: "40px",
                objectFit: "contain",
                borderRadius: "6px",
                margin: "2px 10px",
                cursor: "pointer",
              }}
            />
          </a>
        </div>
      </header>
      
      {loading && <p className="loading">Loading data, please wait...</p>}
     
      {error && <p className="error">{error}</p>}
      
      {!loading && data?.length === 0 && (
        <div className="no-data">
          <img
            src={noDataImage}
            alt="No data available"
            className="no-data-image"
          />
          <p>No data available for the current date.</p>
        </div>
      )}
      
      <div className="rtl-table-layout">
        <div className="table-row">
          {!loading && data?.length > 0 && (
            <div className="rtl-table-container rtl-main-table-container">
              <table className="rtl-data-table horizontal-table rtl-main-table">
                <thead>
                  <tr>
                    <th style={{ minWidth: "150px" }}>Details</th>
                    {data.map((_, index) => (
                      <th key={index} style={{ minWidth: "120px" }}>{displayWarehouse}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Object.keys(columnMappings).map((key, rowIndex) => (
                    <tr key={rowIndex}>
                      <th className="rtl-header-column">{columnMappings[key]}</th>
                      {data.map((item, colIndex) => (
                        <td key={colIndex}>
                          {item[key] !== null && item[key] !== undefined ? item[key] : "-"}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          
          <div className="rtl-secondary-tables">
            {!loading && realTimeLineActivityNonSeed?.length > 0 && (
              <div className="rtl-table-container" style={{ width: "100%", overflowX: "auto" }}>
                <table className="rtl-data-table horizontal-table">
                  <thead>
                    <tr>
                      <th colSpan="5" className="rtl-table-header">
                        Work Order Details
                      </th>
                    </tr>
                    <tr>
                      <th>Name of the Material</th>
                      <th>Dosage</th>
                      <th>UOM</th>
                      <th>Available Quantity</th>
                      <th>Consumption</th>
                    </tr>
                  </thead>
                  <tbody>
                    {realTimeLineActivityNonSeed.map((item, index) => (
                      <tr key={index}>
                        <td>{item.Fields || "-"}</td>
                        <td>{item.Dosoge || "-"}</td>
                        <td>{item.UOM || "-"}</td>
                        <td>{item.PendingQty || "-"}</td>
                        <td>{item.Consumption || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            
            {!loading && realTimeLineActivityData?.length > 0 && (
              <div className="rtl-table-container" style={{ width: "100%", overflowX: "auto" }}>
                <table className="rtl-data-table horizontal-table">
                  <thead>
                    <tr>
                      <th colSpan="4" className="rtl-table-header">
                        Input Information
                      </th>
                    </tr>
                    <tr>
                      <th>Input Batch</th>
                      <th>Quantity Actual</th>
                      <th>Field Variety</th>
                      <th>Plant Variety</th>
                    </tr>
                  </thead>
                  <tbody>
                    {realTimeLineActivityData.map((item, index) => (
                      <tr key={index}>
                        <td>{item.InputeBatch || "-"}</td>
                        <td>{item.OutputeQty || "-"}</td>
                        <td>{item.fieldv || "-"}</td>
                        <td>{item.PlantV || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
      
      <div
        style={{
          position: "fixed",
          bottom: "40px",
          left: 0,
          right: 0,
          color: "#022fe3",
          padding: "12px 0",
          overflow: "hidden",
          whiteSpace: "nowrap",
          fontFamily: "monospace",
          fontSize: "25px",
          fontWeight: "700",
          zIndex: 1000,
        }}
      >
        <div
          style={{
            display: "inline-block",
            animation: "scrollRightToLeft 35s linear infinite",
            whiteSpace: "nowrap",
            paddingLeft: "100%",
          }}
        >
          <span>{scrollingText}</span>
        </div>

        <style>
          {`
            @keyframes scrollRightToLeft {
              0% {
                transform: translateX(0%);
              }
              100% {
                transform: translateX(-100%);
              }
            }
          `}
        </style>
      </div>
    </div>
  );
};

export default RealTimeLineActivity;